import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Bell, XCircle, CalendarClock, MessageCircle, Clock, Link2, Receipt, Building2, Gift } from "lucide-react";
import LedgerDialog from "@/components/LedgerDialog";
import { formatMoney } from "@/lib/currency";
import { Link } from "react-router-dom";

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }); } catch { return s; }
}
function fmtDateTime(s) {
  if (!s) return "Never";
  try { return new Date(s).toLocaleString("en-IN"); } catch { return s; }
}

const STATUS_COLORS = {
  active: "border-emerald-200 bg-emerald-50 text-emerald-700",
  upcoming: "border-sky-200 bg-sky-50 text-sky-700",
  grace: "border-amber-200 bg-amber-50 text-amber-800",
  expired: "border-rose-200 bg-rose-50 text-rose-700",
};

export default function RenewalsDashboard() {
  const [orgs, setOrgs] = useState([]);
  const [pubs, setPubs] = useState([]);
  const [pers, setPers] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [pubId, setPubId] = useState("");
  const [perId, setPerId] = useState("");
  const [window, setWindow] = useState("30");
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);

  const [renewTarget, setRenewTarget] = useState(null);
  const [renewPlanId, setRenewPlanId] = useState("");
  const [renewMethod, setRenewMethod] = useState("UPI");
  const [renewAmount, setRenewAmount] = useState(0);
  const [renewChequeNo, setRenewChequeNo] = useState("");
  const [renewChequeBank, setRenewChequeBank] = useState("");
  const [backfill, setBackfill] = useState(false);
  const [submittingRenew, setSubmittingRenew] = useState(false);

  const [discontinueTarget, setDiscontinueTarget] = useState(null);
  const [ledgerSid, setLedgerSid] = useState(null);

  useEffect(() => { (async () => { try { const { data } = await api.get("/organizations/active-list"); setOrgs(data); } catch {} })(); }, []);
  useEffect(() => {
    setPubId(""); setPerId(""); setPubs([]); setPers([]); setData(null);
    if (!orgId) return;
    (async () => { try { const { data } = await api.get("/publication-houses/active-list", { params: { organization_id: orgId } }); setPubs(data); } catch {} })();
  }, [orgId]);
  useEffect(() => {
    setPerId(""); setPers([]); setData(null);
    if (!pubId) return;
    (async () => {
      try {
        const { data } = await api.get("/periodicals", { params: { publication_house_id: pubId, page: 1, page_size: 100 } });
        setPers(data.items.filter((x) => x.status === "active"));
      } catch {}
    })();
  }, [pubId]);
  useEffect(() => {
    if (!perId) { setData(null); setPlans([]); return; }
    load();
    (async () => {
      try {
        const { data } = await api.get("/subscription-plans", { params: { periodical_id: perId } });
        setPlans(data.filter((p) => (p.effective_status || p.status) === "active"));
      } catch {}
    })();
    // eslint-disable-next-line
  }, [perId, window]);

  const load = async () => {
    setData(null);
    try {
      const { data } = await api.get("/renewals", { params: { periodical_id: perId, window, page: 1, page_size: 100 } });
      setData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); setData({ items: [], total: 0 }); }
  };

  const openRenew = (row) => {
    setRenewTarget(row);
    setRenewPlanId(row.plan_id || "");
    setRenewMethod("UPI");
    setRenewAmount(row.price || 0);
    setRenewChequeNo(""); setRenewChequeBank("");
    setBackfill(false);
  };

  const submitRenew = async () => {
    if (!renewPlanId) { toast.error("Select a plan"); return; }
    setSubmittingRenew(true);
    try {
      const { data } = await api.post(`/subscribers/${renewTarget.subscriber_id}/renew`, {
        new_plan_id: renewPlanId,
        payment_method: renewMethod,
        payment_amount: Number(renewAmount) || 0,
        cheque_number: renewMethod === "Cheque" ? renewChequeNo : null,
        cheque_bank: renewMethod === "Cheque" ? renewChequeBank : null,
        backfill,
        renewal_type: "manual_renewal",
      });
      toast.success(`Renewed · new term ${data.new_start_date} → ${data.new_end_date}`);
      setRenewTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSubmittingRenew(false); }
  };

  const sendReminder = async (row, channel = "whatsapp") => {
    try {
      const { data } = await api.post(`/subscribers/${row.subscriber_id}/remind`, null, { params: { channel } });
      const url = data?.renewal_link;
      if (url) {
        try { await navigator.clipboard.writeText(url); } catch {}
        toast.success(`MOCKED: ${channel} reminder logged · link copied`);
      } else {
        toast.success(`MOCKED: ${channel} reminder logged to ${row.mobile_no}`);
      }
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const copyRenewalLink = async (row) => {
    try {
      const { data } = await api.post(`/subscribers/${row.subscriber_id}/renewal-link`);
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success(`Renewal link copied (valid ${data.expires_in_days}d)`);
      } catch {
        // Clipboard not allowed (insecure context / focus). Show in toast as last resort.
        toast.success(`Renewal link: ${data.url}`);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const discontinue = async () => {
    try {
      await api.patch(`/subscribers/${discontinueTarget.subscriber_id}/discontinue`);
      toast.success("Marked as discontinued");
      setDiscontinueTarget(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const runAlertsNow = async () => {
    try {
      const { data } = await api.post("/renewals-meta/run-alerts-now");
      toast.success(`Alerts enqueued (mocked): 15d=${data["15d"]} · 5d=${data["5d"]} · 0d=${data["0d"]} · +2d=${data["2d_post"]}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="renewals-page" className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">Module 06 · Subscription Management</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">Renewals</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">Prevent service gaps. Identify expiring subscribers and extend seamlessly. Daily cron logs reminders (WhatsApp/SMS/Email dispatch is MOCKED — integrate a provider later).</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild data-testid="renewals-bulk-org-btn" className="rounded-sm">
            <Link to="/renewals/bulk"><Building2 size={14} className="mr-1.5" />Bulk Org Renewal</Link>
          </Button>
          <Button variant="outline" onClick={runAlertsNow} data-testid="renewals-run-alerts" className="rounded-sm" disabled={!perId}>
            <Bell size={14} className="mr-1.5" />Run alerts now
          </Button>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-1 gap-3 rounded-sm border border-slate-200 bg-white p-4 sm:grid-cols-4">
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Organization</Label>
          <Select value={orgId} onValueChange={setOrgId}>
            <SelectTrigger data-testid="ren-org-select" className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Publication House</Label>
          <Select value={pubId} onValueChange={setPubId} disabled={!orgId}>
            <SelectTrigger data-testid="ren-pub-select" className="rounded-sm"><SelectValue placeholder={orgId ? "Select" : "—"} /></SelectTrigger>
            <SelectContent>{pubs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Periodical <span className="text-rose-600">*</span></Label>
          <Select value={perId} onValueChange={setPerId} disabled={!pubId}>
            <SelectTrigger data-testid="ren-per-select" className="rounded-sm"><SelectValue placeholder={pubId ? "Select" : "—"} /></SelectTrigger>
            <SelectContent>{pers.map((p) => <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Expiry Window</Label>
          <Select value={window} onValueChange={setWindow}>
            <SelectTrigger data-testid="ren-window-select" className="rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Next 7 days</SelectItem>
              <SelectItem value="15">Next 15 days</SelectItem>
              <SelectItem value="30">Next 30 days</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {!perId ? (
        <div className="rounded-sm border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
          <CalendarClock className="mx-auto mb-3 text-slate-400" size={28} />
          Select a periodical to see subscribers in the chosen expiry window.
        </div>
      ) : (
        <div className="rounded-sm border border-slate-200 bg-white">
          <Table data-testid="ren-table" className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Subscriber</TableHead>
                <TableHead>Current Plan</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Days</TableHead>
                <TableHead>Last Reminder</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null && [...Array(5)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
              ))}
              {data && data.items.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-slate-500">No subscribers in this window. 🎉</TableCell></TableRow>
              )}
              {data?.items.map((it) => (
                <TableRow key={it.subscriber_id} data-testid={`ren-row-${it.subscriber_id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{it.display_name}</div>
                    <div className="font-mono text-[11px] text-slate-500">{it.subscriber_code} · {it.mobile_no}</div>
                    {it.occasion && <div className="mt-0.5 text-[10px] uppercase tracking-wider text-amber-700">🎁 {it.occasion}</div>}
                    {it.notify_target?.routed_to === "referrer" && (
                      <div data-testid={`gift-route-${it.subscriber_id}`} className="mt-1 inline-flex max-w-full items-center gap-1 truncate rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-900" title={`Reminder will be sent to the original gift-giver: ${it.notify_target.to_name} (${it.notify_target.to_mobile})`}>
                        <Gift size={10} className="shrink-0" />
                        <span className="truncate">→ {it.notify_target.to_name} · {it.notify_target.to_mobile}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-slate-900">{it.plan_name || "—"}</div>
                    <div className="text-xs text-slate-500">{it.plan_total_months}m · {formatMoney(it.price, it.currency)}</div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700">{fmtDate(it.end_date)}</TableCell>
                  <TableCell>
                    <span className={`font-mono text-sm ${it.days_remaining < 0 ? "text-rose-700" : it.days_remaining <= 7 ? "text-amber-700" : "text-slate-700"}`}>
                      {it.days_remaining >= 0 ? `+${it.days_remaining}` : it.days_remaining}d
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {it.last_reminder_at ? (
                      <div><div className="font-mono">{it.last_reminder_type}</div><div className="text-slate-400">{fmtDateTime(it.last_reminder_at)}</div></div>
                    ) : <span className="text-slate-400">Never</span>}
                  </TableCell>
                  <TableCell>
                    <span className={`rounded-sm border px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[it.effective_status] || "border-slate-200 bg-slate-50 text-slate-700"}`}>
                      {it.effective_status === "grace" ? `Grace (+${it.grace_period_days}d)` : it.effective_status}
                    </span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap" style={{ minWidth: 220 }}>
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" onClick={() => openRenew(it)} data-testid={`ren-renew-${it.subscriber_id}`} className="h-8 rounded-sm bg-[#002FA7] text-white hover:bg-[#00227A]">
                        <RefreshCw size={13} className="mr-1" />Renew
                      </Button>
                      <Button size="icon" variant="ghost" title="Send WhatsApp reminder (mocked) · copies one-click renewal link" onClick={() => sendReminder(it, "whatsapp")} data-testid={`ren-remind-${it.subscriber_id}`} className="h-8 w-8 rounded-sm text-emerald-600 hover:bg-emerald-50"><MessageCircle size={16} /></Button>
                      <Button size="icon" variant="ghost" title="Copy one-click renewal link" onClick={() => copyRenewalLink(it)} data-testid={`ren-link-${it.subscriber_id}`} className="h-8 w-8 rounded-sm text-[#002FA7] hover:bg-[#002FA7]/10"><Link2 size={16} /></Button>
                      <Button size="icon" variant="ghost" title="Receipts & Ledger" onClick={() => setLedgerSid(it.subscriber_id)} data-testid={`ren-ledger-${it.subscriber_id}`} className="h-8 w-8 rounded-sm text-amber-600 hover:bg-amber-50"><Receipt size={16} /></Button>
                      <Button size="icon" variant="ghost" title="Mark discontinued" onClick={() => setDiscontinueTarget(it)} data-testid={`ren-discontinue-${it.subscriber_id}`} className="h-8 w-8 rounded-sm text-slate-500 hover:bg-slate-100"><XCircle size={16} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Renew modal */}
      <Dialog open={!!renewTarget} onOpenChange={(o) => !o && setRenewTarget(null)}>
        <DialogContent data-testid="ren-renew-modal" className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle>Renew subscription</DialogTitle>
            <DialogDescription>
              {renewTarget?.display_name} · {renewTarget?.subscriber_code}
              <div className="mt-1 text-xs text-slate-500">
                Current ends <span className="font-medium text-slate-700">{fmtDate(renewTarget?.end_date)}</span>
                {renewTarget?.days_remaining >= 0
                  ? <span className="ml-2 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] text-emerald-700">Seamless: starts day after current end</span>
                  : <span className="ml-2 rounded-sm bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">Gap renewal: starts 1st of next month</span>}
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">New Plan <span className="text-rose-600">*</span></Label>
              <Select value={renewPlanId} onValueChange={(v) => { setRenewPlanId(v); const p = plans.find((x) => x.id === v); if (p) setRenewAmount((p.price || 0) * (1 + (p.tax_percentage || 0) / 100)); }}>
                <SelectTrigger data-testid="renew-plan-select" className="rounded-sm"><SelectValue placeholder="Select plan" /></SelectTrigger>
                <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.plan_name} · {p.total_months}m · {formatMoney((p.price || 0) * (1 + (p.tax_percentage || 0) / 100), p.currency)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Payment Method</Label>
                <Select value={renewMethod} onValueChange={setRenewMethod}>
                  <SelectTrigger data-testid="renew-method-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Cash", "Cheque", "UPI", "Bank Transfer"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Amount</Label>
                <Input type="number" step="0.01" value={renewAmount} onChange={(e) => setRenewAmount(e.target.value)} data-testid="renew-amount-input" className="rounded-sm font-mono" />
              </div>
              {renewMethod === "Cheque" && (
                <>
                  <div><Label className="text-sm">Cheque No.</Label><Input value={renewChequeNo} onChange={(e) => setRenewChequeNo(e.target.value)} className="rounded-sm font-mono" /></div>
                  <div><Label className="text-sm">Bank</Label><Input value={renewChequeBank} onChange={(e) => setRenewChequeBank(e.target.value)} className="rounded-sm" /></div>
                </>
              )}
            </div>
            {renewTarget && renewTarget.days_remaining < 0 && (
              <label className="flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50/60 px-3 py-2 text-sm text-amber-800">
                <input type="checkbox" checked={backfill} onChange={(e) => setBackfill(e.target.checked)} data-testid="renew-backfill" />
                Back-fill missing issues (new term starts day after old expiry instead of 1st of next month)
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenewTarget(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitRenew} disabled={submittingRenew || !renewPlanId} data-testid="renew-submit-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
              <Clock size={14} className="mr-1.5" />
              {submittingRenew ? "Renewing…" : "Confirm renewal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discontinue confirm */}
      <Dialog open={!!discontinueTarget} onOpenChange={(o) => !o && setDiscontinueTarget(null)}>
        <DialogContent className="max-w-md rounded-sm border-slate-300" data-testid="ren-discontinue-modal">
          <DialogHeader>
            <DialogTitle>Mark as Discontinued?</DialogTitle>
            <DialogDescription>
              {discontinueTarget?.display_name} will be marked as discontinued. Active and upcoming terms will be cancelled. Historical records remain.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscontinueTarget(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={discontinue} data-testid="discontinue-confirm" className="rounded-sm bg-rose-600 text-white hover:bg-rose-700">
              Yes, discontinue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LedgerDialog subscriberId={ledgerSid} open={!!ledgerSid} onOpenChange={(o) => !o && setLedgerSid(null)} />
    </div>
  );
}
